/**
 * Pure helpers for interpreting @capgo/capacitor-native-biometric error codes.
 *
 * Mirrors the plugin's `BiometricAuthError` enum (kept in sync with the
 * plugin's README / definitions.d.ts). Extracted from the service layer so the
 * copy is unit-testable without importing Capacitor.
 *
 * Why this matters: the plugin's Android `isAvailable` checks BIOMETRIC_WEAK,
 * but its prompt requests BIOMETRIC_STRONG (+ DEVICE_CREDENTIAL when fallback
 * is allowed). Devices with only weak-class face unlock and no fingerprint
 * report "available" yet the prompt errors immediately with
 * BIOMETRICS_UNAVAILABLE / NOT_ENROLLED — without decoding the code, the user
 * only ever sees a generic "check didn't pass", i.e. "biometrics don't work".
 */

export const BIOMETRIC_ERROR = {
  UNKNOWN: 0,
  BIOMETRICS_UNAVAILABLE: 1,
  USER_LOCKOUT: 2,
  BIOMETRICS_NOT_ENROLLED: 3,
  USER_TEMPORARY_LOCKOUT: 4,
  AUTHENTICATION_FAILED: 10,
  APP_CANCEL: 11,
  INVALID_CONTEXT: 12,
  NOT_INTERACTIVE: 13,
  PASSCODE_NOT_SET: 14,
  SYSTEM_CANCEL: 15,
  USER_CANCEL: 16,
  USER_FALLBACK: 17,
} as const

export type BiometricVerifyResult =
  | { ok: true }
  | {
      ok: false
      code?: number
      cancelled: boolean
      /**
       * The attempt outlived the UI hang budget but the native prompt may
       * still be live — a late success is still honored by the service.
       * Not a real failure; callers should keep retry + password reachable.
       */
      timedOut?: boolean
      message: string
    }

/** Pull the numeric plugin error code off a rejected verifyIdentity call. */
export function extractBiometricErrorCode(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null || !('code' in err)) return undefined
  const raw = (err as { code?: unknown }).code
  const code = Number(raw)
  return Number.isFinite(code) ? code : undefined
}

/**
 * Cancel-type outcomes (user dismissed, app/system interrupted — e.g. an
 * incoming call cancelling the OS sheet). Not a failure; don't alarm the user.
 */
export function isBiometricCancelCode(code: number | undefined): boolean {
  return (
    code === BIOMETRIC_ERROR.APP_CANCEL ||
    code === BIOMETRIC_ERROR.SYSTEM_CANCEL ||
    code === BIOMETRIC_ERROR.USER_CANCEL ||
    code === BIOMETRIC_ERROR.USER_FALLBACK
  )
}

/** Human copy for a failed biometric verify, keyed by plugin error code. */
export function describeBiometricError(code: number | undefined, label: string): string {
  switch (code) {
    case BIOMETRIC_ERROR.APP_CANCEL:
    case BIOMETRIC_ERROR.SYSTEM_CANCEL:
    case BIOMETRIC_ERROR.USER_CANCEL:
    case BIOMETRIC_ERROR.USER_FALLBACK:
      return 'Verification was cancelled.'
    case BIOMETRIC_ERROR.USER_LOCKOUT:
    case BIOMETRIC_ERROR.USER_TEMPORARY_LOCKOUT:
      return `Too many failed attempts. ${label} is temporarily locked \u2014 try again later or use your device passcode.`
    case BIOMETRIC_ERROR.BIOMETRICS_UNAVAILABLE:
      return `${label} isn\u2019t usable on this device right now. Use your device passcode, or sign out and use your password.`
    case BIOMETRIC_ERROR.BIOMETRICS_NOT_ENROLLED:
      return `No ${label} is set up on this device. Enroll it in system Settings, or use your device passcode.`
    case BIOMETRIC_ERROR.PASSCODE_NOT_SET:
      return 'No device passcode is set, so this action can\u2019t be verified on this device.'
    case BIOMETRIC_ERROR.AUTHENTICATION_FAILED:
      return `${label} didn\u2019t match. Please try again.`
    default:
      return `Couldn\u2019t verify with ${label}. Please try again.`
  }
}

/**
 * Copy for the locked state when the device has no biometrics AND no passcode
 * (nothing local to verify against) — the only way forward is Cognito password.
 */
export function describeNoLocalAuthState(label: string): string {
  return `${label} and a device passcode aren\u2019t set up on this device. Set one up in system Settings, or use your password to sign back in.`
}

/**
 * Copy when the native verify callback never returns (Capacitor hang) — keep
 * password fallback in the sentence so the unlock sheet always has an escape.
 */
export function describeBiometricHang(label: string): string {
  return `${label} didn\u2019t respond. Try again, or use your password.`
}
